import React, { useState, useRef, useEffect } from 'react';
import { cn } from '../../utils/cn';
import { ChevronDown, ChevronUp, X } from 'lucide-react';

const SingleSelectDropdown = ({
  label,
  options = [],
  value = null,
  onChange,
  placeholder = "Select an option...",
  disabled = false,
  className = '',
  error,
  required = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Get selected option object
  const selectedOption = options?.find(option => option?.value === value) || null;

  // Handle option selection
  const handleOptionSelect = (optionValue) => {
    if (!onChange) return;
    
    onChange(optionValue);
    setIsOpen(false);
    setFocusedIndex(-1);
  };

  // Clear selection
  const handleClear = (e) => {
    e?.stopPropagation();
    if (!onChange) return;
    onChange(null);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e?.key === 'ArrowDown' || e?.key === 'ArrowUp' || e?.key === 'Enter' || e?.key === ' ') {
        e?.preventDefault();
        setIsOpen(true);
        setFocusedIndex(0);
      }
      return;
    }

    switch (e?.key) {
      case 'ArrowDown':
        e?.preventDefault();
        setFocusedIndex(prev => 
          prev < options?.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e?.preventDefault();
        setFocusedIndex(prev => 
          prev > 0 ? prev - 1 : options?.length - 1
        );
        break;
      case 'Enter': case' ':
        e?.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < options?.length) {
          handleOptionSelect(options?.[focusedIndex]?.value);
        }
        break;
      case 'Escape':
        e?.preventDefault();
        setIsOpen(false);
        setFocusedIndex(-1);
        inputRef?.current?.focus();
        break;
      default:
        break;
    }
  };

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setIsOpen(false);
        setFocusedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll focused item into view
  useEffect(() => {
    if (isOpen && focusedIndex >= 0 && listRef?.current) {
      const focusedElement = listRef?.current?.children?.[focusedIndex];
      if (focusedElement) {
        focusedElement?.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [focusedIndex, isOpen]);

  return (
    <div className={cn("relative w-full", className)}>
      {/* Label */}
      {label && (
        <label className="block text-sm font-medium text-text mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Main Input Container */}
      <div className="relative" ref={dropdownRef}>
        <div
          ref={inputRef}
          className={cn(
            // Base styles with theme-aware colors
            "min-h-[40px] w-full rounded-md border bg-surface px-3 py-2",
            "cursor-pointer transition-colors duration-200",
            // Focus and hover states
            "focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent",
            "hover:border-muted",
            // Error state
            error ? "border-red-500 focus:border-red-500 focus:ring-red-500/20" : "border-border",
            // Disabled state
            disabled && "cursor-not-allowed opacity-50 bg-muted/20"
          )}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          onKeyDown={handleKeyDown}
          tabIndex={disabled ? -1 : 0}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        >
          <div className="flex items-center pr-8">
            {/* Selected Option Display */}
            {selectedOption ? (
              <span className="text-text text-sm">
                {selectedOption?.label}
              </span>
            ) : (
              <span className="text-muted text-sm">{placeholder}</span>
            )}
          </div>

          {/* Right Side Controls */}
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {/* Clear Button */}
            {selectedOption && (
              <button
                type="button"
                onClick={handleClear}
                className="text-muted hover:text-text focus:outline-none p-1"
                aria-label="Clear selection"
              >
                <X size={16} />
              </button>
            )}
            
            {/* Dropdown Arrow */}
            {isOpen ? (
              <ChevronUp className="h-4 w-4 text-muted" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted" />
            )}
          </div>
        </div>

        {/* Dropdown List */}
        {isOpen && (
          <div 
            className={cn(
              "absolute z-50 w-full mt-1 bg-surface border border-border rounded-md shadow-popover",
              // Scrollable if more than 6 options (240px max height)
              options?.length > 6 && "max-h-60 overflow-auto"
            )}
            role="listbox"
          >
            <div ref={listRef}>
              {options?.map((option, index) => {
                const isSelected = value === option?.value;
                const isFocused = index === focusedIndex;
                
                return (
                  <div
                    key={option?.value}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2 text-sm cursor-pointer transition-colors",
                      // Hover and focus styles with theme-aware colors
                      isFocused && "bg-accent/10",
                      !isFocused && "hover:bg-muted/10",
                      // Selected state
                      isSelected && "bg-accent/10 text-accent"
                    )}
                    onClick={() => handleOptionSelect(option?.value)}
                    role="option"
                    aria-selected={isSelected}
                  >
                    {/* Radio Button Indicator */}
                    <div 
                      className={cn(
                        "w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors",
                        isSelected 
                          ? "bg-accent border-accent" :"border-border bg-surface"
                      )}
                    >
                      {isSelected && (
                        <div className="w-2 h-2 bg-surface rounded-full" />
                      )}
                    </div>
                    
                    {/* Option Label */}
                    <span className="flex-1 text-text">{option?.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default SingleSelectDropdown;